import React, { useState } from 'react';
import { IListingData } from '@models/listing';
import StaticLayout from '@components/layout/StaticLayout';
import { useLazyQuery, useQuery } from '@apollo/client';
import { filterQuery, listQuery } from './queries';
import { dataToLovs, mapToListingData } from './utils';
import { filterSectionsContent, headers } from './content';
import { IListingFilterWidgetSection } from '@components/widgets/listing-filter-widget';
import ListingFilterWidget from '@components/widgets/listing-filter-widget/ListingFilterWidget';
import { IAbstractRecord } from '@models';
import _ from 'lodash';
import { getFilter, setFilter } from '@utils/filter-utils';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import { toast } from 'react-toastify';
import { DEFAULT_ERROR_TEXT } from '@utils';
import { useAppSelector } from '@redux/hooks';
import CoverDrawer from '../policy-cover-drawer/CoverDrawer';
import { capitalizeFirstCharacter } from '@utils/formatting-utils';
import DateService from '@services/DateService';
import { IEnhancedMenuItem } from '@components/table/types';
import ListingWidget from '@components/widgets/listing-widget/ListingWidget';
import { IFilterModel } from '@components/widgets/listing-widget';

const PAGE_CONTEXT = 'Covers';
const FILTER_SESSION_KEY = 'coversFilter';

const CoversListingPage: React.FC = () => {
  const user = useAppSelector((state) => state.user);

  const [loading, setLoading] = useState<boolean>(false);
  const [policyCoversDrawerOpen, setPolicyCoversDrawerOpen] =
    useState<boolean>(false);

  const isAdmin = user.userRoles.includes('Insurance-Admin');
  const actions: IEnhancedMenuItem[] = isAdmin
    ? [
        {
          name: 'new',
          title: '+ New',
          onClick: () => {
            setPolicyCoversDrawerOpen(true);
          },
        },
      ]
    : [];

  const initialFilterValues =
    Object.keys(getFilter(FILTER_SESSION_KEY)).length > 0
      ? getFilter(FILTER_SESSION_KEY)
      : {
          line: [],
          SelectedStatuses: [],
          createdOn: [],
        };
  const [filterValues, setFilterValues] = useState<IFilterModel>({
    namedFilters: initialFilterValues,
    pagination: {
      pageSize: 10,
      pageNumber: 1,
    },
    descending: true,
  });

  const [filterSections, setFilterSections] =
    useState<IListingFilterWidgetSection[]>();

  const [tableData, setTableData] = useState<IListingData>({
    pagedItems: {},
    pageSize: 10,
    pageNumber: 1,
    totalCount: 0,
  });

  const [loadCoversLazy] = useLazyQuery(listQuery());
  const filterResponse = useQuery(filterQuery());

  const getListingData = (filter = filterValues) => {
    if (!filter) {
      setLoading(false);
      return {};
    }

    setLoading(true);
    setFilter(filter, FILTER_SESSION_KEY);
    setFilterValues(filter);

    const { namedFilters, pagination } = filter;

    const formattedDateRange = DateService.formatDateRangeBackend(
      namedFilters.createdOn
    );
    return loadCoversLazy({
      variables: {
        keywordSearch: filter.searchKey,
        currentPage: pagination.pageNumber || 1,
        currentPageSize: pagination.pageSize || 10,
        Attribute:
          capitalizeFirstCharacter(filter.orderBy || '') ||
          'PlanConfigManagement_Subline_Name',
        Descending: filter.descending,
        SelectedLineId: namedFilters.line || [],
        SelectedStatuses: namedFilters.status || null,
        FromCreatedOn: formattedDateRange?.[0],
        ToCreatedOn: formattedDateRange?.[1],
      },
    })
      .then(({ data }) => {
        if (data) {
          setTableData(mapToListingData(data));
          filterResponse.refetch().then(({ data: filterData }) => {
            setFilterSections(
              filterSectionsContent(dataToLovs(filterData), namedFilters)
            );
          });
        }
      })
      .catch(() => {
        toast.error(
          <ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handlePageChange = (
    page: number,
    filterModel = filterValues
  ): Promise<void> => {
    return new Promise<void>(() =>
      getListingData({
        ...filterModel,
        pagination: {
          ...filterModel.pagination,
          pageNumber: page,
        },
      })
    );
  };

  const onFilterUpdate = (newFilters: IAbstractRecord) => {
    const updatedFilters = {
      ...filterValues,
      namedFilters: newFilters,
    };
    if (!_.isEqual(updatedFilters, filterValues)) {
      getListingData(updatedFilters);
    }
  };

  const renderMainChildren = () => (
    <>
      <ListingWidget
        title="Policy Covers"
        name="covers"
        data={tableData}
        tableSettings={{
          headers,
        }}
        orderByAscendingByDefault
        defaultOrderByColumn="planConfigManagement_PolicyCover_Name"
        loading={loading}
        pageContext={PAGE_CONTEXT}
        onPageChange={(filterModel) => {
          const newFilterModel = {
            ...filterValues,
            ...filterModel,
          };
          const page = filterModel.pagination.pageNumber;
          return handlePageChange(page, newFilterModel);
        }}
        actions={actions}
        usePagination
        disableSelection
      />
      {policyCoversDrawerOpen && (
        <CoverDrawer
          open={policyCoversDrawerOpen}
          onClose={() => setPolicyCoversDrawerOpen(false)}
          onSuccess={() => {
            handlePageChange(0);
          }}
          isLineNameDisabled={false}
          canNavigateToDetailsPage={true}
        />
      )}
    </>
  );

  const renderFilter = () =>
    filterSections && (
      <ListingFilterWidget
        name=""
        disabled={loading}
        filters={filterSections}
        onApplyFilter={onFilterUpdate}
      />
    );

  return (
    <StaticLayout
      name="Policy Covers"
      leftChildren={renderFilter()}
      mainChildren={renderMainChildren()}
    />
  );
};

export default CoversListingPage;
